import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { useCart } from '@/hooks/useCart';
import { getStoreCapabilities } from '@/lib/accountType';
import { listPublishedCustomPages } from '@/hooks/useCustomPages';
import { useDisplayCurrency } from '@/hooks/useDisplayCurrency';
import { CURRENCIES, normalizeCurrency } from '@/lib/currency';

interface SidebarLink {
  path: string;
  label: string;
  icon: string;
}

/**
 * Barre latérale de la boutique publique.
 * Bouton flottant (en bas à gauche) qui ouvre un menu latéral listant les
 * sections réellement disponibles — dont les Hébergements dès que le marchand
 * a publié un bien. N'affiche que les liens utiles (aucun lien mort).
 */
export default function TenantSidebar() {
  const { tenant } = useTenant();
  const location = useLocation();
  const { count: cartCount } = useCart();
  const [open, setOpen] = useState(false);
  const [counts, setCounts] = useState({ products: 0, services: 0, hebergements: 0, formations: 0, events: 0 });
  const [customPages, setCustomPages] = useState<{ slug: string; title: string }[]>([]);
  const nativeCurrency = normalizeCurrency(tenant?.monaie || 'MAD');
  const { currency, change } = useDisplayCurrency(nativeCurrency);

  useEffect(() => {
    if (!tenant) return;
    let cancelled = false;
    (async () => {
      try {
        const [prodRes, svcRes, hebRes, formRes, evRes] = await Promise.all([
          supabase.from('product_items').select('id', { count: 'exact', head: true }).eq('idcommerce', tenant.id).eq('status', 'active'),
          supabase.from('nospartenairesservices').select('id', { count: 'exact', head: true }).eq('idcommerce', tenant.id),
          supabase.from('hebergement_properties').select('id', { count: 'exact', head: true }).eq('merchant_id', tenant.id).eq('published', true).eq('status', 'active'),
          supabase.from('formations').select('id', { count: 'exact', head: true }).eq('id_formateur', tenant.id).or('status.is.null,status.eq.published,status.eq.active'),
          supabase.from('events').select('id', { count: 'exact', head: true }).eq('id_user', String(tenant.id)),
        ]);
        if (cancelled) return;
        setCounts({
          products: prodRes.count || 0,
          services: svcRes.count || 0,
          hebergements: hebRes.count || 0,
          formations: formRes.count || 0,
          events: evRes.count || 0,
        });
      } catch {
        /* silencieux */
      }
      try {
        const pages = await listPublishedCustomPages(Number(tenant.id));
        if (!cancelled) setCustomPages(pages.map((p) => ({ slug: p.slug, title: p.title })));
      } catch {
        /* silencieux */
      }
    })();
    return () => { cancelled = true; };
  }, [tenant]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const caps = getStoreCapabilities(tenant?.type);

  const links: SidebarLink[] = [
    { path: '/', label: 'Accueil', icon: 'ri-home-line' },
  ];
  if (caps.products && counts.products > 0) {
    links.push({ path: '/products', label: 'Nos produits', icon: 'ri-shopping-bag-line' });
  }
  if (caps.services && counts.services > 0) {
    links.push({ path: '/services', label: 'Nos services', icon: 'ri-service-line' });
  }
  if (counts.hebergements > 0) {
    links.push({ path: '/hebergements', label: 'Hébergements', icon: 'ri-hotel-bed-line' });
  }
  if (counts.formations > 0) {
    links.push({ path: '/formations', label: 'Formations', icon: 'ri-graduation-cap-line' });
  }
  if (counts.events > 0) {
    links.push({ path: '/evenements', label: 'Événements', icon: 'ri-calendar-event-line' });
  }
  if (caps.products) {
    links.push({ path: '/cart', label: 'Mon panier', icon: 'ri-shopping-cart-line' });
  }
  links.push({ path: '/page/contact', label: 'Contact', icon: 'ri-mail-line' });

  const customLinks = customPages.map((p) => ({
    path: `/p/${p.slug}`,
    label: p.title || p.slug,
    icon: 'ri-pages-line',
  }));

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const renderLink = (link: SidebarLink, onNavigate: () => void) => (
    <Link
      key={link.path}
      to={link.path}
      onClick={onNavigate}
      className={`zifek-store-menu-item flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors no-underline cursor-pointer ${
        isActive(link.path)
          ? 'bg-primary-50 text-primary-700'
          : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
      }`}
    >
      <i className={`${link.icon} text-lg w-5 h-5 flex items-center justify-center flex-shrink-0`}></i>
      <span className="truncate">{link.label}</span>
    </Link>
  );

  return (
    <>
      {/* Bouton flottant (bas gauche) */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Ouvrir le menu de la boutique"
        className="fixed bottom-5 left-5 z-[70] w-12 h-12 flex items-center justify-center rounded-full bg-foreground-950 text-background-50 shadow-lg hover:bg-foreground-800 transition-colors cursor-pointer"
      >
        <i className="ri-menu-2-line text-xl"></i>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 bg-black/30 z-[80]" onClick={() => setOpen(false)} />
          <aside className="zifek-store-menu fixed top-0 left-0 bottom-0 z-[90] w-72 max-w-[85vw] bg-background-50 border-r border-background-200/70 flex flex-col">
            <div className="flex items-center justify-between px-4 h-14 border-b border-background-200/70 flex-shrink-0">
              <span className="zifek-store-menu-title text-sm font-semibold text-foreground-950 flex items-center gap-2">
                <i className="ri-store-2-line text-primary-500"></i>
                Menu
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer"
                aria-label="Fermer"
              >
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
              {links.map((link) => renderLink(link, () => setOpen(false)))}

              {customLinks.length > 0 && (
                <>
                  <div className="zifek-store-menu-label px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-wider text-foreground-300">
                    Pages
                  </div>
                  {customLinks.map((link) => renderLink(link, () => setOpen(false)))}
                </>
              )}

              <div className="zifek-store-menu-label px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-wider text-foreground-300">
                Devise d&apos;affichage
              </div>
              <div className="px-1 pb-2">
                <select
                  value={currency}
                  onChange={(e) => change(e.target.value)}
                  className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-700 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  {CURRENCIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.flag} {c.code} — {c.label}
                    </option>
                  ))}
                </select>
                <p className="px-1 mt-1.5 text-[10px] text-foreground-400">
                  Les prix s&apos;affichent dans la devise choisie (taux en direct).
                </p>
              </div>
            </nav>

            {caps.products && cartCount > 0 && (
              <div className="flex-shrink-0 p-3 border-t border-background-200/70">
                <Link
                  to="/cart"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-center gap-2 h-11 rounded-full bg-primary-500 text-background-50 text-sm font-semibold no-underline whitespace-nowrap"
                >
                  <i className="ri-shopping-cart-line"></i>
                  Voir mon panier ({cartCount})
                </Link>
              </div>
            )}
          </aside>
        </>
      )}
    </>
  );
}